// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import {
  IAction,
  ActionType,
  raiseAction,
  IActionTypeSetPeriod,
  ActionGraphCommand,
} from "actions";
import { TrendMonitor as ITrendMonitor } from "shared/types/cloudtypes";
import TrendMonitorModel from "model/trendmonitor";
import { TimePeriod } from "types";
import { IState } from "states/states";
import { getSiteSelection, toDropdownItems } from "guiutils";

import Dropdown from "view/comp/Dropdown";

interface ITrendMonitorSelectExtProps {
  className?: string;
}

interface ITrendMonitorSelectProps {
  selectedMonitor: number | undefined;
  trendMonitors: ITrendMonitor[];
}

const mapStateToProps = (state: IState, ownProps: ITrendMonitorSelectExtProps) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  const props: ITrendMonitorSelectProps = {
    selectedMonitor: getSiteSelection(state)?.trendMonitor,
    trendMonitors: site?.cloudSettings?.trendMonitor ?? [],
  };
  return props;
};

interface ITrendMonitorSelectDispatch {
  onTrendMonitorChanged: (trendMonitors: ITrendMonitor[], value: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendMonitorSelectDispatch = {
    onTrendMonitorChanged: (trendMonitors: ITrendMonitor[], value: number) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_TRENDMONITOR, value as number));
      // also update timePeriod
      const selectedTrendMonitor = trendMonitors[value as number];
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.SET_PERIOD,
        timePeriod: selectedTrendMonitor.period as unknown as TimePeriod,
        selectedDate: Date.now() / 1000,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      // load data
      dispatch(raiseAction(ActionType.ACT_UPDATE_TREND));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select trend monitor",
  },
});

class TrendMonitorSelectComponent extends React.Component<
  ITrendMonitorSelectExtProps & ITrendMonitorSelectProps & ITrendMonitorSelectDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const idxs = TrendMonitorModel.getActiveSortedTrendMonitorIdxs(this.props.trendMonitors);
    const items = toDropdownItems(
      idxs,
      "selectedTrendMonitor",
      (name, value) => this.props.onTrendMonitorChanged(this.props.trendMonitors, value),
      (id) => this.props.trendMonitors[id].title,
    );

    return (
      <div id="trendmonitorselect">
        <Dropdown
          items={items}
          caption={this.context.formatMessage(messages.selectCaption)}
          curItem={
            this.props.trendMonitors[this.props.selectedMonitor!]?.title ??
            this.props.trendMonitors[idxs[0]]?.title ??
            this.context.formatMessage(messages.selectCaption)
          }
        />
      </div>
    );
  }
}

const TrendMonitorSelect = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TrendMonitorSelectComponent);

export default TrendMonitorSelect;
